/**
 * Service Provider Service
 * Handles service provider authentication and bidding operations
 */

import { apiClient } from './api';
import type {
  Plan,
  PlanBidWithPricing,
  CreateBidRequest,
} from '@calwmp/shared';
import {
  ensureMagicLinkValidationResponse,
  ensureAvailablePlansResponse,
  ensureBidResponse,
  ensureBidsResponse,
  ensureSubscriptionResponse,
} from '@/lib/api/type-guards';

/**
 * Plans response
 */
export interface AvailablePlansResponse {
  plans: Plan[];
  total: number;
}

/**
 * Bids response
 */
export interface BidsResponse {
  bids: PlanBidWithPricing[];
  total: number;
}

/**
 * Subscription tier type
 */
export type SubscriptionTier = 'free' | 'basic' | 'pro' | 'enterprise';

/**
 * Subscription status type
 */
export type SubscriptionStatus = 'active' | 'cancelled' | 'past_due' | 'trialing';

/**
 * Service Provider Subscription
 */
export interface Subscription {
  id: string;
  service_provider_id: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  service_area_counties: string; // JSON array of county IDs
  monthly_job_limit: number | null;
  transaction_fee_percent: number;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: number;
  created_at: number;
  updated_at: number;
}

/**
 * Subscription response
 */
export interface SubscriptionResponse {
  subscription: Subscription;
}

/**
 * Bid submission response
 */
export interface BidResponse {
  bid: PlanBidWithPricing;
}

/**
 * Dispatch DTOs (Backend Response Types)
 */

/**
 * Driver DTO from backend
 */
export interface DriverDto {
  id: string;
  name: string;
  avatar?: string;
  status: 'available' | 'on_route' | 'on_job' | 'off_duty';
  current_job_id?: string;
  current_job_name?: string;
  current_job_eta?: string;
  assigned_jobs_count: number;
  available_at?: string;
}

/**
 * Dispatch Job DTO from backend
 */
export interface DispatchJobDto {
  id: string;
  job_id: string;
  project_name: string;
  project_address: string;
  scheduled_start: string;
  scheduled_end: string;
  status: string;
  assigned_driver_id?: string;
  assigned_driver_name?: string;
  assigned_driver_avatar?: string;
  estimated_duration_minutes: number;
  container_types: string[];
}

/**
 * Today's Dispatch Response
 */
export interface TodaysDispatchResponse {
  date: string;
  jobs: DispatchJobDto[];
  drivers: DriverDto[];
}

/**
 * Schedule Response
 */
export interface ScheduleResponse {
  jobs: DispatchJobDto[];
  start_date: string;
  end_date: string;
}

/**
 * Dispatch Assignment Response
 */
export interface DispatchAssignment {
  job_id: string;
  driver_id: string;
  assigned_at: string;
}

/**
 * Optimized Route Response
 */
export interface OptimizedRoute {
  driver_id: string;
  jobs: Array<{
    job_id: string;
    sequence: number;
  }>;
  total_distance_miles: number;
  total_duration_minutes: number;
  optimized_at: string;
}

/**
 * Weight Ticket DTOs (Backend Response Types)
 */

/**
 * Ticket Photo DTO from backend
 */
export interface TicketPhoto {
  id: string;
  url: string;
  thumbnail_url: string;
  caption?: string;
  uploaded_at: string;
}

/**
 * Weight Ticket DTO from backend
 */
export interface WeightTicket {
  id: string;
  ticket_number: string;
  job_id: string;
  job_name?: string;
  status: 'pending' | 'approved' | 'rejected';
  material_type: string;
  facility_name: string;
  facility_address?: string;
  facility_certification?: string;
  gross_weight: number; // lbs
  tare_weight: number; // lbs
  net_weight: number; // lbs (calculated by backend)
  weighed_at: string; // ISO timestamp
  driver_name: string;
  vehicle_number: string;
  container_size?: string;
  created_at: number; // Unix timestamp
  submitted_at?: string; // ISO timestamp
  reviewed_at?: number; // Unix timestamp
  approved_by_name?: string;
  rejection_reason?: string;
  billing_rate?: number; // $/ton
  invoiced?: boolean;
  photos: TicketPhoto[];
}

/**
 * DTO for creating a weight ticket
 */
export interface CreateTicketDto {
  job_id: string;
  material_type: string;
  facility_name: string;
  gross_weight: number; // in pounds
  tare_weight: number; // in pounds
  weighed_at: string; // ISO timestamp
  driver_name: string;
  vehicle_number: string;
  container_size?: string;
  notes?: string;
}

/**
 * Tickets list response
 */
export interface TicketsResponse {
  tickets: WeightTicket[];
  total: number;
}

/**
 * Ticket filters
 */
export interface TicketFilters {
  status?: string;
  job_id?: string;
  date_range?: string;
}

/**
 * Ticket report parameters
 */
export interface TicketReportParams {
  start_date: string;
  end_date: string;
}

/**
 * Ticket report response
 */
export interface TicketReport {
  total_tickets: number;
  total_weight_lbs: number;
  by_material: Array<{
    material_type: string;
    weight_lbs: number;
  }>;
  by_status: Array<{
    status: string;
    count: number;
  }>;
  weight_over_time?: Array<{
    date: string;
    weight_lbs: number;
  }>;
}

// ============================================
// DISPATCHER JOB CREATION TYPES
// ============================================

/**
 * Input for creating a job as dispatcher
 * Matches backend CreateJobAsDispatcherSchema
 */
export interface CreateJobAsDispatcherInput {
  // Service Details
  serviceType: string;
  containerSize: string;
  materialType: string;
  scheduledDate: string; // ISO datetime
  timeWindow?: string; // 'morning' | 'afternoon' | 'evening' | 'anytime'
  priority?: string; // 'standard' | 'urgent'

  // Location (required)
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    latitude?: number;
    longitude?: number;
  };

  // Customer snapshot (required)
  customerSnapshot: {
    customerId?: string;
    name: string;
    phone: string;
    email?: string;
  };

  // Notes
  specialInstructions?: string;
}

/**
 * Job with approval status
 */
export interface JobPendingApproval {
  id: string;
  project_name: string;
  project_address: string;
  scheduled_date: string;
  status: 'pending_price_approval';
  created_by_user_id: string;
  created_by_user_name?: string;
  base_price: number;
  adjusted_price: number;
  deviation_percentage: number;
  price_adjustments: Array<{
    lineItem: string;
    originalAmount: number;
    adjustedAmount: number;
    reason: string;
  }>;
  created_at: string;
}

/**
 * Pending approvals response
 */
export interface PendingApprovalsResponse {
  jobs: JobPendingApproval[];
  total: number;
}

/**
 * Job response (for dispatcher operations)
 */
export interface JobResponse {
  id: string;
  project_name: string;
  project_address: string;
  scheduled_date: string;
  status: string;
  [key: string]: unknown;
}

export const serviceProviderService = {
  /**
   * Request magic link for service provider authentication
   */
  async requestMagicLink(email: string): Promise<void> {
    await apiClient.post('/auth/service-provider/request-magic-link', {
      email,
    });
  },

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<{ token: string; provider: unknown }> {
    const data = await apiClient.post<unknown>(
      '/auth/service-provider/login',
      { email, password }
    );

    // Type guard for login response
    if (
      !data ||
      typeof data !== 'object' ||
      !('token' in data) ||
      typeof data.token !== 'string' ||
      !('provider' in data)
    ) {
      throw new Error('Invalid login response');
    }

    const response = data as { token: string; provider: unknown };

    // Store token in localStorage with service provider prefix
    // Set expiry to 24 hours from now (matching backend JWT expiry)
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24);

    localStorage.setItem('sp_auth_token', response.token);
    localStorage.setItem('sp_auth_expires_at', expiresAt.toISOString());

    return response;
  },

  /**
   * Validate magic link token and get authentication token
   */
  async validateMagicLink(token: string): Promise<{
    success: boolean;
    token: string;
    expires_at: string;
    provider: {
      id: string;
      email: string;
      company_name: string;
      role: string;
    };
  }> {
    const data = await apiClient.post<unknown>(
      '/auth/service-provider/validate-magic-link',
      { token }
    );

    // Validate response structure
    const response = ensureMagicLinkValidationResponse(data);

    // Store token in localStorage with service provider prefix
    localStorage.setItem('sp_auth_token', response.token);
    localStorage.setItem('sp_auth_expires_at', response.expires_at);

    return response;
  },

  /**
   * Get authentication token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem('sp_auth_token');
  },

  /**
   * Check if service provider is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('sp_auth_token');
    const expiresAt = localStorage.getItem('sp_auth_expires_at');

    if (!token || !expiresAt) {
      return false;
    }

    // Check if token is expired
    const expiryDate = new Date(expiresAt);
    const now = new Date();

    if (now >= expiryDate) {
      this.logout();
      return false;
    }

    return true;
  },

  /**
   * Logout service provider
   */
  logout(): void {
    localStorage.removeItem('sp_auth_token');
    localStorage.removeItem('sp_auth_expires_at');
  },

  /**
   * Get available plans to bid on
   */
  async getAvailablePlans(params?: {
    county?: string;
    limit?: number;
    offset?: number;
  }): Promise<AvailablePlansResponse> {
    const token = this.getToken();
    const queryParams = new URLSearchParams();

    if (params?.county) queryParams.append('county', params.county);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const url = `/service-provider/available-plans${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const data = await apiClient.get<unknown>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response structure
    const response = ensureAvailablePlansResponse(data);

    return {
      plans: response.plans as Plan[],
      total: response.total,
    };
  },

  /**
   * Submit a bid on a plan
   */
  async submitBid(planId: string, bid: CreateBidRequest): Promise<PlanBidWithPricing> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      `/service-provider/plans/${planId}/bid`,
      bid,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    const response = ensureBidResponse(data);

    return response.bid as PlanBidWithPricing;
  },

  /**
   * Get service provider's bids
   */
  async getBids(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<BidsResponse> {
    const token = this.getToken();
    const queryParams = new URLSearchParams();

    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const url = `/service-provider/bids${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const data = await apiClient.get<unknown>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response structure
    const response = ensureBidsResponse(data);

    return {
      bids: response.bids as PlanBidWithPricing[],
      total: response.total,
    };
  },

  /**
   * Update a bid
   */
  async updateBid(bidId: string, updates: Partial<CreateBidRequest>): Promise<PlanBidWithPricing> {
    const token = this.getToken();

    const data = await apiClient.patch<unknown>(
      `/service-provider/bids/${bidId}`,
      updates,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    const response = ensureBidResponse(data);

    return response.bid as PlanBidWithPricing;
  },

  /**
   * Withdraw a bid
   */
  async withdrawBid(bidId: string, reason?: string): Promise<PlanBidWithPricing> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      `/service-provider/bids/${bidId}/withdraw`,
      { reason },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    const response = ensureBidResponse(data);

    return response.bid as PlanBidWithPricing;
  },

  /**
   * Get service provider's subscription
   */
  async getSubscription(): Promise<SubscriptionResponse> {
    const token = this.getToken();

    const data = await apiClient.get<unknown>('/service-provider/subscription', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response structure
    const response = ensureSubscriptionResponse(data);

    return {
      subscription: response.subscription as Subscription,
    };
  },

  // ============================================
  // DISPATCH OPERATIONS
  // ============================================

  /**
   * Get today's dispatch board
   */
  async getTodaysDispatch(date?: string): Promise<TodaysDispatchResponse> {
    const token = this.getToken();
    const queryParams = new URLSearchParams();

    if (date) queryParams.append('date', date);

    const url = `/service-provider/dispatch/today${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const data = await apiClient.get<unknown>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid dispatch response');
    }

    const response = data as Record<string, unknown>;

    if (
      typeof response.date !== 'string' ||
      !Array.isArray(response.jobs) ||
      !Array.isArray(response.drivers)
    ) {
      throw new Error('Invalid dispatch response structure');
    }

    return {
      date: response.date,
      jobs: response.jobs as DispatchJobDto[],
      drivers: response.drivers as DriverDto[],
    };
  },

  /**
   * Get schedule for date range
   */
  async getSchedule(params: { startDate: string; endDate: string; driverId?: string; status?: string }): Promise<ScheduleResponse> {
    const token = this.getToken();
    const queryParams = new URLSearchParams();

    queryParams.append('start_date', params.startDate);
    queryParams.append('end_date', params.endDate);
    if (params.driverId) queryParams.append('driver_id', params.driverId);
    if (params.status) queryParams.append('status', params.status);

    const url = `/service-provider/dispatch/schedule?${queryParams.toString()}`;

    const data = await apiClient.get<unknown>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid schedule response');
    }

    const response = data as Record<string, unknown>;

    if (
      !Array.isArray(response.jobs) ||
      typeof response.start_date !== 'string' ||
      typeof response.end_date !== 'string'
    ) {
      throw new Error('Invalid schedule response structure');
    }

    return {
      jobs: response.jobs as DispatchJobDto[],
      start_date: response.start_date,
      end_date: response.end_date,
    };
  },

  /**
   * Assign driver to job
   */
  async assignDriver(jobId: string, driverId: string): Promise<DispatchAssignment> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      '/service-provider/dispatch/assign',
      { job_id: jobId, driver_id: driverId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid assignment response');
    }

    return data as DispatchAssignment;
  },

  /**
   * Reassign driver
   */
  async reassignDriver(jobId: string, newDriverId: string): Promise<DispatchAssignment> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      '/service-provider/dispatch/reassign',
      { job_id: jobId, new_driver_id: newDriverId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid reassignment response');
    }

    return data as DispatchAssignment;
  },

  /**
   * Optimize route for driver
   */
  async optimizeRoute(driverId: string, jobIds: string[]): Promise<OptimizedRoute> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      '/service-provider/dispatch/routes/optimize',
      { driver_id: driverId, job_ids: jobIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid optimization response');
    }

    return data as OptimizedRoute;
  },

  // ============================================
  // WEIGHT TICKET OPERATIONS
  // ============================================

  /**
   * Get weight tickets with optional filters
   */
  async getTickets(filters?: TicketFilters): Promise<TicketsResponse> {
    const token = this.getToken();
    const queryParams = new URLSearchParams();

    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.job_id) queryParams.append('job_id', filters.job_id);
    if (filters?.date_range) queryParams.append('date_range', filters.date_range);

    const url = `/service-provider/tickets${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const data = await apiClient.get<unknown>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid tickets response');
    }

    const response = data as Record<string, unknown>;

    if (!Array.isArray(response.tickets) || typeof response.total !== 'number') {
      throw new Error('Invalid tickets response structure');
    }

    return {
      tickets: response.tickets as WeightTicket[],
      total: response.total,
    };
  },

  /**
   * Get single weight ticket
   */
  async getTicket(ticketId: string): Promise<WeightTicket> {
    const token = this.getToken();

    const data = await apiClient.get<unknown>(
      `/service-provider/tickets/${ticketId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('ticket' in data)) {
      throw new Error('Invalid ticket response');
    }

    return (data as { ticket: WeightTicket }).ticket;
  },

  /**
   * Create weight ticket
   */
  async createTicket(ticket: CreateTicketDto): Promise<WeightTicket> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      '/service-provider/tickets',
      ticket,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('ticket' in data)) {
      throw new Error('Invalid create ticket response');
    }

    return (data as { ticket: WeightTicket }).ticket;
  },

  /**
   * Update weight ticket
   */
  async updateTicket(
    ticketId: string,
    updates: Partial<CreateTicketDto>
  ): Promise<WeightTicket> {
    const token = this.getToken();

    const data = await apiClient.patch<unknown>(
      `/service-provider/tickets/${ticketId}`,
      updates,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('ticket' in data)) {
      throw new Error('Invalid update ticket response');
    }

    return (data as { ticket: WeightTicket }).ticket;
  },

  /**
   * Approve weight ticket
   */
  async approveTicket(ticketId: string): Promise<WeightTicket> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      `/service-provider/tickets/${ticketId}/approve`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('ticket' in data)) {
      throw new Error('Invalid approve ticket response');
    }

    return (data as { ticket: WeightTicket }).ticket;
  },

  /**
   * Reject weight ticket
   */
  async rejectTicket(ticketId: string, reason: string): Promise<WeightTicket> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      `/service-provider/tickets/${ticketId}/reject`,
      { reason },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('ticket' in data)) {
      throw new Error('Invalid reject ticket response');
    }

    return (data as { ticket: WeightTicket }).ticket;
  },

  /**
   * Upload photo to weight ticket
   */
  async uploadTicketPhoto(ticketId: string, photo: File): Promise<TicketPhoto> {
    const token = this.getToken();

    const formData = new FormData();
    formData.append('photo', photo);

    const data = await apiClient.post<unknown>(
      `/service-provider/tickets/${ticketId}/photos`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type, browser will set it with boundary
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('photo' in data)) {
      throw new Error('Invalid upload photo response');
    }

    return (data as { photo: TicketPhoto }).photo;
  },

  /**
   * Get tickets pending review
   */
  async getPendingReviewTickets(): Promise<WeightTicket[]> {
    const token = this.getToken();

    const data = await apiClient.get<unknown>(
      '/service-provider/tickets/pending-review',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('tickets' in data)) {
      throw new Error('Invalid pending review response');
    }

    const response = data as { tickets: unknown };

    if (!Array.isArray(response.tickets)) {
      throw new Error('Invalid pending review response structure');
    }

    return response.tickets as WeightTicket[];
  },

  /**
   * Get weight ticket report
   */
  async getTicketReport(params: TicketReportParams): Promise<TicketReport> {
    const token = this.getToken();
    const queryParams = new URLSearchParams();

    queryParams.append('start_date', params.start_date);
    queryParams.append('end_date', params.end_date);

    const url = `/service-provider/tickets/report?${queryParams.toString()}`;

    const data = await apiClient.get<unknown>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid report response');
    }

    return data as TicketReport;
  },

  // ============================================
  // DISPATCHER JOB CREATION OPERATIONS
  // ============================================

  /**
   * Create job as dispatcher with price adjustments and overrides
   * Requires dispatcher or manager permission
   *
   * POST /api/v1/provider/jobs/create-as-dispatcher
   */
  async createJobAsDispatcher(input: CreateJobAsDispatcherInput): Promise<JobResponse> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      '/provider/jobs/create-as-dispatcher',
      input,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('job' in data)) {
      throw new Error('Invalid create job response');
    }

    const response = data as { job: unknown };

    if (!response.job || typeof response.job !== 'object') {
      throw new Error('Invalid job data in response');
    }

    return response.job as JobResponse;
  },

  /**
   * Approve or reject a price adjustment
   * Requires manager permission
   *
   * POST /api/v1/provider/jobs/:jobId/approve-price
   */
  async approvePriceAdjustment(
    jobId: string,
    decision: 'approve' | 'reject',
    notes?: string
  ): Promise<JobResponse> {
    const token = this.getToken();

    const data = await apiClient.post<unknown>(
      `/provider/jobs/${jobId}/approve-price`,
      { decision, notes },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object' || !('job' in data)) {
      throw new Error('Invalid approve price response');
    }

    const response = data as { job: unknown };

    if (!response.job || typeof response.job !== 'object') {
      throw new Error('Invalid job data in response');
    }

    return response.job as JobResponse;
  },

  /**
   * Get jobs pending price approval
   * Requires manager permission
   *
   * GET /api/v1/provider/jobs/pending-approval
   */
  async getPendingApprovals(): Promise<PendingApprovalsResponse> {
    const token = this.getToken();

    const data = await apiClient.get<unknown>(
      '/provider/jobs/pending-approval',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid pending approvals response');
    }

    const response = data as Record<string, unknown>;

    if (!Array.isArray(response.jobs) || typeof response.total !== 'number') {
      throw new Error('Invalid pending approvals response structure');
    }

    return {
      jobs: response.jobs as JobPendingApproval[],
      total: response.total,
    };
  },
};
